import { makeAutoObservable } from 'mobx';
import type { RootStore } from './root-store';
import i18n from '../../translation/i18n';
import { PersonalAssetImportTypeEnum } from '../../defs/api';
// eslint-disable-next-line import/no-cycle
import { PersonalAssetStore } from './personal-asset-store';

interface ImportResultWarning {
    message: string;
    link?: string;
}

export class PersonalAssetImportDialogStore {
    rootStore: RootStore;

    personalAssetStore: PersonalAssetStore;

    opened = false;

    file: File | null = null;

    loading = false;

    importResultMessage: string | null = null;

    importResultWarnings: ImportResultWarning[] = [];

    importedRowsCount: number = 0;

    constructor(personalAssetStore: PersonalAssetStore) {
        this.rootStore = personalAssetStore.rootStore;
        this.personalAssetStore = personalAssetStore;

        makeAutoObservable(this, {
            rootStore: false,
            personalAssetStore: false,
        });
    }

    openImportDialog(): void {
        this.opened = true;

        this.rootStore.alertStore.setModalPlacement();
    }

    closeImportDialog(): void {
        this.opened = false;
        this.rootStore.alertStore.setDefaultPlacement();
        this.resetUploadedFile();
    }

    resetUploadedFile(): void {
        this.file = null;
    }

    setFile(file: File) {
        this.file = file;
    }

    startLoading(): void {
        this.loading = true;
    }

    stopLoading(): void {
        this.loading = false;
    }

    async importFile(importType: PersonalAssetImportTypeEnum, postImport?: () => Promise<void>) {
        this.importResultMessage = null;
        this.importResultWarnings = [];

        try {
            if (!this.file) {
                throw new Error('No uploaded file');
            }
            this.startLoading();

            const response = await this.rootStore.apiClient.personalAssetsApi.personalAssetControllerImport({
                importType,
                personalAssetsFile: this.file,
            });

            if (!response) {
                throw new Error('No response from trade import');
            }

            if (response.invalidFormat) {
                this.importResultWarnings.push({ message: i18n.t(`blocks.personalFinances.imported.failed`) });
            } else {
                this.importResultMessage = i18n.t(`blocks.personalFinances.imported.successfully`);
            }

            this.closeImportDialog();
        } catch (e) {
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
            // eslint-disable-next-line no-console
            console.error(e);
        } finally {
            await postImport?.();
            this.stopLoading();
        }
    }

    clearImportResultMessage() {
        this.importResultMessage = null;
    }

    setImportResultMessage(text: string) {
        this.importResultMessage = text;
    }

    clearImportResultWarnings() {
        this.importResultWarnings = [];
    }
}
